import React, { useState, useCallback } from 'react';
import BRAND_COLORS from './brandColors.js';

// Bottom-sheet confirmation modal. Returns null when closed so each open
// remounts the sheet element and re-triggers the slide-up CSS animation.
export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  variant = 'default',
}) {
  if (!isOpen) return null;

  const confirmBg =
    variant === 'danger' ? BRAND_COLORS.goji : BRAND_COLORS.teal;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center confirm-backdrop-enter"
      style={{ backgroundColor: 'rgba(41, 56, 93, 0.5)' }}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-modal shadow-elevation-4 p-6 pb-10 confirm-sheet-enter"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="w-10 h-1 bg-gray-200 rounded-chip mx-auto mb-6" />
        {title && (
          <h2 className="text-title-md font-bold text-gray-900 mb-2">
            {title}
          </h2>
        )}
        {message && (
          <p className="text-body-md text-gray-600 mb-8">{message}</p>
        )}
        <div className="flex flex-col gap-3">
          <button
            onClick={onConfirm}
            className="w-full h-touch-md rounded-btn font-semibold text-body-lg text-white active:opacity-80 transition-opacity"
            style={{ backgroundColor: confirmBg }}
          >
            {confirmLabel}
          </button>
          <button
            onClick={onCancel}
            className="w-full h-touch-md rounded-btn font-semibold text-body-lg text-gray-600 bg-gray-100 active:opacity-80 transition-opacity"
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// Pairs with <ConfirmModal {...confirmProps}>. Call confirm({ title, message,
// confirmLabel, cancelLabel, variant }) to open the sheet; the returned Promise
// resolves true on confirm and false on cancel (incl. backdrop tap).
export function useConfirm() {
  const [state, setState] = useState({ isOpen: false });

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      setState({
        isOpen: true,
        title: opts.title,
        message: opts.message,
        confirmLabel: opts.confirmLabel,
        cancelLabel: opts.cancelLabel,
        variant: opts.variant,
        onConfirm: () => {
          setState((s) => ({ ...s, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setState((s) => ({ ...s, isOpen: false }));
          resolve(false);
        },
      });
    });
  }, []);

  return { confirm, confirmProps: state };
}
